import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Loader2, Lock, Play, Check, Star, Clock, Tag, PlayCircle, ChevronRight,
  Infinity as InfinityIcon, ListChecks, CheckCircle2, X,
} from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { track, gaEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { SiteLayout } from "@/components/site/SiteLayout";
import { VideoPlayer } from "@/components/site/VideoPlayer";
import { Button } from "@/components/ui/button";
import { ContactInstructor } from "@/components/site/ContactInstructor";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";

const COVER_FALLBACK = "https://images.pexels.com/photos/7697208/pexels-photo-7697208.jpeg";

const DEFAULT_OUTCOMES = [
  { title: "Build The Foundation", text: "Learn how to create and position your initial guidelines correctly." },
  { title: "Control Your Guidelines", text: "Understand spacing, sections and how each guideline affects the final fade." },
  { title: "Master The Lever", text: "Learn precise clipper lever control and guard selection." },
  { title: "Remove Visible Lines", text: "Identify and eliminate stubborn lines without creating new ones." },
  { title: "Perfect The Blend", text: "Develop smoother transitions from skin to darker areas." },
  { title: "Detail The Final Result", text: "Professional finishing techniques that make a fade look polished." },
];

const AUDIENCE = [
  { title: "Beginner Barbers", text: "Build strong fundamentals from the beginning." },
  { title: "Working Barbers", text: "Improve consistency and refine your blending technique." },
  { title: "Self-Taught Barbers", text: "Turn scattered techniques into a repeatable system." },
  { title: "Barbers Seeking Cleaner Results", text: "Better control over transitions, guidelines and detailing." },
];

const reveal = { hidden: { opacity: 0, y: 26 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06 } }) };

function Eyebrow({ children, center }) {
  return (
    <div className={`mb-4 flex items-center gap-3 ${center ? "justify-center" : ""}`}>
      <span className="h-px w-8 bg-[#DCA846]" />
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DCA846]">{children}</span>
      {center && <span className="h-px w-8 bg-[#DCA846]" />}
    </div>
  );
}

export default function CourseDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [buying, setBuying] = useState(false);
  const [preview, setPreview] = useState(null); // {lessonId, source}
  const [showSticky, setShowSticky] = useState(false);
  const purchaseRef = useRef(null);
  const viewedRef = useRef(null);

  const { data: course, isLoading } = useQuery({
    queryKey: ["course", slug],
    queryFn: async () => (await api.get(`/catalog/courses/${slug}`)).data,
  });

  useEffect(() => {
    if (course) {
      track("course_view", { course_id: course.id });
      document.title = `${course.meta_title || course.title} — Epic Fade Academy`;
      // GA4 view_item — once per course (guarded against re-renders / refetch).
      if (viewedRef.current !== course.id) {
        viewedRef.current = course.id;
        const p = course.sale_price != null && course.sale_price < course.price ? course.sale_price : course.price;
        gaEvent("view_item", {
          currency: "CAD",
          value: Number(p) || 0,
          items: [{ item_id: course.id, item_name: course.title, price: Number(p) || 0 }],
        });
      }
    }
  }, [course]);

  const allLessons = useMemo(() => (course?.sections || []).flatMap((s) => s.lessons), [course]);
  const firstPreview = useMemo(() => allLessons.find((l) => l.is_free_preview), [allLessons]);

  // mobile sticky CTA visibility
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 520);
    let visible = false;
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.2 });
    if (purchaseRef.current) io.observe(purchaseRef.current);
    const handler = () => setShowSticky(window.scrollY > 520 && !visible);
    window.addEventListener("scroll", handler, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", handler); io.disconnect(); };
  }, [course]);

  if (isLoading) return <SiteLayout><div className="flex justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" /></div></SiteLayout>;
  if (!course) return <SiteLayout><div className="py-40 text-center text-zinc-400">Course not found.</div></SiteLayout>;

  const hasSale = course.sale_price != null && course.sale_price < course.price;
  const displayPrice = hasSale ? course.sale_price : course.price;
  const lessonCount = allLessons.length;
  const outcomes = course.learning_outcomes?.length ? course.learning_outcomes : DEFAULT_OUTCOMES;
  const highlights = course.instructor_highlights?.length ? course.instructor_highlights
    : ["15+ Years Experience", "Licensed Canadian Barber", "Real Barbershop Techniques", "Beginner-Friendly Teaching"];

  const finalPrice = () => {
    let p = displayPrice;
    if (couponInfo) p = couponInfo.discount_type === "percentage" ? p * (1 - couponInfo.value / 100) : Math.max(0, p - couponInfo.value);
    return Math.max(0, p);
  };
  const priceLabel = course.is_free ? "Free" : `$${Number(displayPrice).toFixed(0)}`;

  const applyCoupon = async () => {
    try {
      const { data } = await api.get("/catalog/coupon/validate", { params: { code: coupon, course_id: course.id } });
      setCouponInfo(data);
      toast.success("Coupon applied");
    } catch (e) { setCouponInfo(null); toast.error(e.response?.data?.detail || "Invalid coupon"); }
  };

  const enroll = async () => {
    if (course.has_access || course.is_free) return nav(`/learn/${course.id}`);
    if (!user) return nav("/login", { state: { from: `/course/${slug}` } });
    track("buy_click", { course_id: course.id });
    setBuying(true);
    try {
      track("checkout_started", { course_id: course.id });
      // GA4 begin_checkout — fires only when a paid checkout actually starts.
      gaEvent("begin_checkout", {
        currency: "CAD",
        value: Number(finalPrice().toFixed(2)),
        items: [{ item_id: course.id, item_name: course.title, price: Number(displayPrice) || 0 }],
      });
      const { data } = await api.post("/payments/checkout", { course_id: course.id, origin_url: window.location.origin, coupon_code: couponInfo ? coupon : null });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setBuying(false); }
  };

  const openPreview = async (lesson) => {
    if (!lesson) return toast.info("No free preview available for this course yet");
    try {
      const { data } = await api.get(`/lessons/${lesson.id}/play`);
      setPreview({ lessonId: lesson.id, source: data, videoType: data.video_type || lesson.video_type });
      // GA4 custom free_lesson_start — only when a FREE preview lesson is actually opened/played.
      if (lesson.is_free_preview) {
        gaEvent("free_lesson_start", {
          lesson_id: lesson.id, lesson_name: lesson.title,
          course_id: course.id, course_name: course.title,
        });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Preview unavailable"); }
  };

  const enrollLabel = course.has_access ? "Start Learning" : course.is_free ? "Watch Free" : `Enroll Now — $${Number(displayPrice).toFixed(0)}`;

  return (
    <SiteLayout>
      {/* 1 — HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={resolveImg(course.cover_image) || COVER_FALLBACK} alt="" className="h-full w-full object-cover opacity-[0.12]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-14 md:py-20">
          <Link to="/store" className="text-sm text-zinc-400 hover:text-[#DCA846]">← Back to store</Link>
          <div className="mt-6 grid items-center gap-12 lg:grid-cols-[1.3fr_1fr]">
            <motion.div initial="hidden" animate="show" variants={reveal}>
              <Eyebrow>{course.skill_level || "Course"}{course.category_id ? "" : ""}</Eyebrow>
              <h1 className="font-display text-4xl font-bold uppercase leading-[0.98] tracking-tighter text-white sm:text-5xl md:text-6xl">
                {course.meta_title ? course.title : `Master The ${course.title}`}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">
                {course.subtitle || course.short_description}
              </p>
              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm text-zinc-300">
                <span className="flex items-center gap-2"><Play className="h-4 w-4 text-[#DCA846]" /> {lessonCount} Video Lessons</span>
                <span className="flex items-center gap-2"><ChevronRight className="h-4 w-4 text-[#DCA846]" /> {course.skill_level || "All Levels"}</span>
                <span className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-[#DCA846]" /> Step-by-Step Training</span>
                <span className="flex items-center gap-2"><InfinityIcon className="h-4 w-4 text-[#DCA846]" /> Lifetime Access</span>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button data-testid="hero-enroll-btn" onClick={enroll} disabled={buying}
                  className="bg-[#DCA846] px-8 py-6 text-black hover:bg-[#C5963B] font-display uppercase tracking-wide rounded-sm gold-glow">
                  {buying ? <Loader2 className="h-5 w-5 animate-spin" /> : enrollLabel}
                </Button>
                {!course.has_access && (
                  <Button data-testid="hero-preview-btn" variant="outline" onClick={() => openPreview(firstPreview)}
                    className="border-white/20 bg-transparent px-8 py-6 text-white hover:bg-white/10 font-display uppercase tracking-wide rounded-sm">
                    <PlayCircle className="mr-2 h-4 w-4" /> Watch Free Preview
                  </Button>
                )}
              </div>
              <div className="mt-5 flex items-center gap-4 text-sm text-zinc-400">
                <span className="flex items-center gap-1 text-[#DCA846]"><Star className="h-4 w-4 fill-[#DCA846]" /> {Number(course.rating || 5).toFixed(1)}</span>
                {course.has_access && <span className="text-emerald-400">✓ You own this course</span>}
              </div>
            </motion.div>

            {/* 9:16 preview */}
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
              className="mx-auto w-full max-w-[300px]">
              <button data-testid="hero-preview-thumb" onClick={() => openPreview(firstPreview)}
                className="group relative block aspect-[9/16] w-full overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 gold-glow">
                <img src={resolveImg(course.thumbnail || course.cover_image) || COVER_FALLBACK} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#DCA846]/50 bg-black/50 backdrop-blur transition-transform group-hover:scale-110">
                    <Play className="h-6 w-6 translate-x-0.5 text-[#DCA846]" />
                  </span>
                </span>
                <span className="absolute bottom-4 left-0 right-0 text-center text-xs uppercase tracking-widest text-white/80">
                  {firstPreview ? "Watch Free Preview" : "Course Preview"}
                </span>
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2 — WHAT YOU'LL MASTER */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24 border-b border-white/5">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
          <Eyebrow>What You'll Master</Eyebrow>
          <h2 className="max-w-2xl font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">Build technique that shows in every cut</h2>
          <p className="mt-4 max-w-2xl text-zinc-400">Build the technical control and visual understanding needed to create cleaner, smoother and more consistent fades.</p>
        </motion.div>
        <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {outcomes.map((o, i) => (
            <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal} className="group">
              <span className="font-display text-2xl text-[#DCA846]/70">{String(i + 1).padStart(2, "0")}</span>
              <div className="mt-2 h-px w-10 bg-[#DCA846]/40 transition-all group-hover:w-16" />
              <h3 className="mt-3 font-display text-lg uppercase tracking-wide text-white">{o.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{o.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3 + 4 — CURRICULUM (real data) */}
      <section className="mx-auto max-w-4xl px-6 py-20 md:py-24 border-b border-white/5">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
          <Eyebrow>Inside The Course</Eyebrow>
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">The complete haircut, start to finish</h2>
          <p className="mt-4 text-zinc-400">Follow the complete haircut from the first guideline to the final detail.</p>
        </motion.div>
        <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
          {allLessons.map((l, i) => (
            <motion.div key={l.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
              data-testid={`curriculum-lesson-${l.id}`}
              className="group flex items-center gap-4 py-4">
              <span className="font-display text-sm text-[#DCA846]/70 tabular-nums w-8">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-base uppercase tracking-wide text-zinc-100">{l.title}</span>
                  {l.is_free_preview && <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] uppercase text-emerald-400">Free Preview</span>}
                  {l.completed && <span className="flex items-center gap-1 text-[10px] uppercase text-[#DCA846]"><Check className="h-3 w-3" /> Completed</span>}
                </div>
              </div>
              {l.duration && <span className="hidden sm:flex items-center gap-1 text-xs text-zinc-500"><Clock className="h-3 w-3" /> {l.duration}</span>}
              {l.playable ? (
                <button data-testid={`preview-play-${l.id}`} onClick={() => (l.is_free_preview && !course.has_access ? openPreview(l) : nav(`/learn/${course.id}?lesson=${l.id}`))}
                  className="flex items-center gap-1 rounded-sm border border-[#DCA846]/40 px-3 py-1.5 text-xs uppercase text-[#DCA846] transition-colors hover:bg-[#DCA846] hover:text-black">
                  <Play className="h-3 w-3" /> {course.has_access ? "Watch" : "Preview"}
                </button>
              ) : (
                <span className="flex items-center gap-1 rounded-sm border border-white/10 px-3 py-1.5 text-xs uppercase text-zinc-500"><Lock className="h-3 w-3" /> Locked</span>
              )}
            </motion.div>
          ))}
          {allLessons.length === 0 && <p className="py-8 text-center text-zinc-500">Lessons coming soon.</p>}
        </div>
      </section>

      {/* 5 — INSTRUCTOR */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24 border-b border-white/5">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="grain relative overflow-hidden rounded-2xl border border-white/10 gold-glow">
              <img src={resolveImg(course.instructor_image) || "https://images.unsplash.com/photo-1717700921740-a1440f3b89a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"}
                alt="Instructor" loading="lazy" className="aspect-[4/5] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
            <Eyebrow>Learn From Experience</Eyebrow>
            <p className="text-xs uppercase tracking-widest text-zinc-500">Meet Your Instructor</p>
            <h2 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
              {course.instructor_name || "Your Master Barber"}
            </h2>
            {course.instructor_title && <p className="mt-1 text-[#DCA846]">{course.instructor_title}</p>}
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              {course.instructor_bio || "Learn professional barbering techniques developed through more than 15 years of hands-on experience behind the chair."}
            </p>
            <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {highlights.map((h) => (
                <div key={h} className="flex items-center gap-3 border-l-2 border-[#DCA846]/40 pl-3">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-[#DCA846]" />
                  <span className="text-sm text-zinc-200">{h}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6 — WHO THIS IS FOR */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24 border-b border-white/5">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
          <Eyebrow>Is This Course For You?</Eyebrow>
        </motion.div>
        <div className="mt-8 grid gap-x-10 gap-y-8 sm:grid-cols-2">
          {AUDIENCE.map((a, i) => (
            <motion.div key={a.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
              className="flex gap-4 border-t border-white/10 pt-6">
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-[#DCA846]" />
              <div>
                <h3 className="font-display text-lg uppercase tracking-wide text-white">{a.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">{a.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 7 — PURCHASE */}
      <section ref={purchaseRef} data-testid="purchase-section" className="mx-auto my-20 max-w-4xl px-6">
        <div className="grain relative overflow-hidden rounded-2xl border border-[#DCA846]/25 bg-gradient-to-b from-zinc-900/70 to-black p-10 text-center md:p-14">
          <Eyebrow center>Enroll Today</Eyebrow>
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
            Ready to master the {course.title.replace(/professional /i, "")}?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-zinc-400">Learn at your own pace. Rewatch every technique and return whenever you need a refresher.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-zinc-300">
            <span className="font-display uppercase">{course.title}</span>
            <span className="flex items-center gap-1"><Play className="h-4 w-4 text-[#DCA846]" /> {lessonCount} Lessons</span>
            <span className="flex items-center gap-1"><InfinityIcon className="h-4 w-4 text-[#DCA846]" /> Lifetime Access</span>
          </div>
          {!course.has_access && !course.is_free && (
            <div className="mx-auto mt-6 flex max-w-sm gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" className="bg-zinc-950 border-white/10 pl-9 text-white" />
              </div>
              <Button data-testid="apply-coupon-btn" variant="outline" onClick={applyCoupon} className="border-white/15 text-white">Apply</Button>
            </div>
          )}
          <div className="mt-6 flex items-baseline justify-center gap-3">
            <span className="font-display text-4xl text-white">{course.is_free ? "Free" : `$${finalPrice().toFixed(0)}`}</span>
            {(hasSale || couponInfo) && !course.is_free && <span className="text-lg text-zinc-500 line-through">${Number(course.price).toFixed(0)}</span>}
          </div>
          <Button data-testid="purchase-enroll-btn" onClick={enroll} disabled={buying}
            className="mt-6 bg-[#DCA846] px-10 py-6 text-black hover:bg-[#C5963B] font-display uppercase tracking-wide rounded-sm gold-glow">
            {buying ? <Loader2 className="h-5 w-5 animate-spin" /> : enrollLabel}
          </Button>
          <p className="mt-3 flex items-center justify-center gap-1 text-xs text-zinc-500"><Lock className="h-3 w-3" /> Secure checkout · Instant access</p>
        </div>
      </section>

      {/* CONTACT INSTRUCTOR */}
      <section className="mx-auto max-w-3xl px-6 pb-16">
        <ContactInstructor context={course.title} course={course.title} />
      </section>

      {/* 8 — MOBILE STICKY CTA */}
      {showSticky && !course.has_access && (
        <div data-testid="mobile-sticky-cta" className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-black/90 backdrop-blur-lg md:hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-display uppercase text-white">{course.title}</p>
              <p className="text-xs text-[#DCA846]">{priceLabel}</p>
            </div>
            <Button data-testid="sticky-enroll-btn" onClick={enroll} className="shrink-0 bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase rounded-sm">
              {course.is_free ? "Watch" : "Enroll Now"}
            </Button>
          </div>
        </div>
      )}

      {/* PREVIEW MODAL */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md border-white/10 bg-black p-4">
          {preview && (
            <VideoPlayer lessonId={preview.lessonId} source={preview.source} videoType={preview.videoType} />
          )}
        </DialogContent>
      </Dialog>
    </SiteLayout>
  );
}
